<script setup lang="ts">
import { computed, provide } from "vue";
import DRMenuItem from "./DRMenuItem.vue";
import type { MenuEntity } from "./menu.types";
import { useRoute } from "vue-router";
import UserState from "@/stores/UserState";
import { filterMenuByRole } from "@/utils/menu.utils";

const props = withDefaults(
  defineProps<{
    menu: MenuEntity[];
    type?: "primary" | "params" | "dark";
    minimal?: boolean;
  }>(),
  {
    type: "primary",
    minimal: undefined,
  }
);

// Provide minimal state to child components
provide(
  "menuMinimal",
  computed(() => props.minimal)
);
const route = useRoute();

const activeRoute = computed(() => {
  const currentRoute = route.name ? (route.name as string) : undefined;
  if (!currentRoute) return undefined;

  // Check if any menu item with matchChildRoutes should be active
  const findMatchingParent = (items: MenuEntity[]): string | undefined => {
    for (const item of items) {
      if (
        item.matchChildRoutes &&
        item.index &&
        currentRoute.startsWith(item.index)
      ) {
        return item.index;
      }
      if (item.children) {
        const match = findMatchingParent(item.children);
        if (match) return match;
      }
    }
    return undefined;
  };

  const parentMatch = findMatchingParent(props.menu);
  return parentMatch || currentRoute;
});

const filterMenu = computed(() => {
  const role = UserState.data?.role;
  if (!role) return [];

  return filterMenuByRole(role, props.menu);
});
</script>

<template>
  <el-menu
    :default-active="activeRoute"
    router
    class="border-0 transparent d-flex flex-column gap-2"
  >
    <DRMenuItem
      v-for="(item, id) in filterMenu"
      :key="'menu-' + id"
      :item="item"
      :type="type"
    >
    </DRMenuItem>
  </el-menu>
</template>
